<script lang="ts">
import Vue from 'vue'
import {Component, Prop, Watch} from 'vue-property-decorator'

@Component({})
export default class SpaceSettingsHelpIcon extends Vue {
  @Prop()
  modal: string

  open = false
}
</script>

<template>
  <span>
    <font-awesome-icon
      @click="open = true"
      icon="info-circle"
      class="fa-fw text-black-50 fa-sm cursor-pointer" />

    <b-modal v-if="modal == 'BASE_STATION'" v-model="open" centered>
      <h2 class="mb-4">Smart Hub Settings</h2>

      <h4>Learning Mode</h4>
      <p>
        Learning Mode allows you to get familiar with your new system, without the stress of
        triggering an emergency event.
      </p>
      <p>
        While Learning Mode is activated, assistance requests from the independent adult will not
        connect with the Response Team, but they will notify caregivers within the Care Circle.
      </p>
      <p>
        Caregivers can elevate an emergency to our Response Team by selecting “Check-In Call,” then
        “Call [location of Smart Hub] Device,” and selecting the “Emergency” button on the call
        screen.
      </p>

      <h4>Voice Sensitivity Levels</h4>

      <p>
        <b>HIGH</b>: On this setting, the device is more likely to hear the voice command,
        “emergency”. Please note, it is also more likely that outside influences such as a TV or
        radio may falsely trigger the device.
      </p>
      <p class="font-weight-bold">NORMAL: Recommended, default.</p>
      <p>
        <b>LOW</b>: On this setting, the device is less likely to hear the voice command,
        “emergency”. This may be appropriate if you often find the Smart Hub falsely being
        triggered.
      </p>
      <p>
        <b>OFF</b>: Turning voice sensitivity off means that the Smart Hub will NOT listen for the
        voice command “emergency” and all emergencies must be triggered by button press on the Smart
        Hub. This is not recommended.
      </p>

      <h4>Voice Command: “Emergency”</h4>

      <p>The voice command "emergency" is the wake word phrase used to trigger your Smart Hub.</p>
      <p>
        When “emergency” is heard by the Smart Hub, repeated at the correct number of intervals
        (2x/3x/4x) during the set listening window, the Smart Hub will trigger an emergency
        response.
      </p>
      <p>
        <b>We recommend setting the interval at 3x.</b> Example: Emergency, emergency, emergency.
      </p>

      <h4>Testing Your Device</h4>
      <p>
        We recommend testing these device settings with your loved ones by selecting each of the
        button press settings and pressing the button at the required interval.
      </p>
      <p>
        To avoid calling Emergency Services when testing, put your system on Learning Mode,
        accessible by clicking the icon on the upper right corner of Device Management. Adjust
        settings as appropriate for your needs.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>

    <b-modal v-if="modal == 'WALABOT'" v-model="open" centered>
      <h2 class="mb-4">Fall Sensor Settings</h2>

      <h4>Voice Sensitivity Levels</h4>

      <p>
        <b>HIGH</b>: On this setting, the device is more likely to hear the voice command,
        “emergency”. Please note, it is also more likely that outside influences such as a TV or
        radio may falsely trigger the device.
      </p>
      <p class="font-weight-bold">NORMAL: Recommended, default.</p>
      <p>
        <b>LOW</b>: On this setting, the device is less likely to hear the voice command,
        “emergency”. This may be appropriate if you often find the Fall Sensor falsely being
        triggered.
      </p>
      <p>
        <b>OFF</b>: Turning voice sensitivity off means that the Fall Sensor will NOT listen for the
        voice command “emergency” and all emergencies must be triggered by button press on the Fall
        Sensor. This is not recommended.
      </p>

      <h4>Voice Command: “Emergency”</h4>

      <p>The voice command "emergency" is the wake word phrase used to trigger your Fall Sensor.</p>
      <p>
        When “emergency” is heard by the Fall Sensor, repeated at the correct number of intervals
        (2x/3x/4x) during the set listening window, the Fall Sensor will trigger an emergency
        response.
      </p>
      <p>
        <b>We recommend setting the interval at 3x.</b> Example: Emergency, emergency, emergency.
      </p>

      <h4>Testing Your Device</h4>
      <p>
        We recommend testing these device settings with your loved ones by selecting each of the
        button press settings and pressing the button at the required interval.
      </p>
      <p>
        To avoid calling Emergency Services when testing, put your system on Learning Mode,
        accessible by clicking the icon on the upper right corner of Device Management. Adjust
        settings as appropriate for your needs.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>

    <b-modal v-if="modal == 'MOTION_SENSOR'" v-model="open" centered>
      <h2 class="mb-4">Motion Sensor Settings</h2>

      <h4>Motion Sensitivity Levels</h4>

      <p><b>Normal</b>: Recommended.</p>
      <p>
        <b>LOW</b>: On this setting, the device is less likely to hear the voice command,
        “emergency”. This may be appropriate if you often find the Fall Sensor falsely being
        triggered.
      </p>
      <p>
        <b>OFF</b>: Turning motion sensitivity off means that the devices will NOT detect any
        motion. This is not recommended.
      </p>

      <h4>Install Help</h4>
      <p>
        Your Motion Sensor monitors mobility and room temperature. Avoid placing this sensor in the
        same room as your other Aloe Care smart devices.
      </p>

      <h4>Placement</h4>
      <ul>
        <li>Place the Motion Sensor in a location with a clear view of the monitored area.</li>
        <li>Avoid placing the Motion Sensor close to a heating/cooling source.</li>
        <li>Avoid placing the Motion Sensor in bright or direct sunlight.</li>
        <li>Place the sensor free of curtains and other obstacles.</li>
        <li>The Motion Sensor must be accessible for battery testing and maintenance.</li>
      </ul>

      <h4>Mounting</h4>
      <p>
        There are several mounting options included with your Motion Sensor. You can mount it flat
        on a ceiling or wall, using the screws, adhesive tape, or magnet, but we highly encourage
        using the provided stand. Insert the stand in the small opening on the underside of the
        sensor. Place the standing sensor on a flat surface, such as a countertop, table, or shelf.
      </p>

      <h4>Resetting</h4>
      <p>
        Resetting your Motion Sensor may be needed in order to eliminate abnormal behavior, such as
        trouble with setup.
      </p>
      <ol>
        <li>
          Remove the front cover the device, taking care to make sure the batteries are still
          inserted correctly.
        </li>
        <li>Locate the small gray button in the middle of the device, between both batteries.</li>
        <li>
          Press and hold the small gray reset button. You will see a series of LED flashes. The LED
          will flash once, then two times in a row, and finally numerous times in a row.
        </li>
        <li>
          Release the button while the LED is flashing numerous times in a row. After you release
          the button, the LED should show one long flash, marking a complete reset.
        </li>
      </ol>

      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>

    <b-modal v-if="modal == 'MPERS' || modal == 'MPERS_GO'" v-model="open" centered>
      <h2 class="mb-4">Mobile Companion Settings</h2>

      <h4>Fall Detection</h4>
      <p><b>NORMAL:</b> Recommended, default</p>
      <p>
        <b>OFF:</b> Turning fall detection off means that the Mobile Companion will NOT
        automatically detect falls and all emergencies must be triggered by button press on the
        device. This is not recommended.
      </p>

      <h4>Testing Your Device</h4>

      <p>
        We recommend testing these device settings with your loved ones by dropping the Mobile
        Companion at varying heights/speeds against different surfaces. Adjust settings as
        appropriate for your needs.*
      </p>

      <h4>Updating Settings</h4>
      <p>
        Please note: it can take up to 5 minutes for your new settings to update on this device.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>

    <b-modal v-if="modal == 'MPERS_EVIEW'" v-model="open" centered>
      <h2 class="mb-4">Mobile Companion 2 Settings</h2>

      <h4>Fall Detection</h4>

      <p>
        <b>HIGH:</b> On this setting, the device is more likely to detect a fall and this may be
        appropriate if you find the device is not sensitive enough. Please note, it is also more
        likely that the device will be falsely triggered on this setting.
      </p>

      <p><b>NORMAL:</b> Recommended, default</p>

      <p>
        <b>LOW:</b> On this setting, the device is less likely to detect a fall. This may be
        appropriate if you often find the device falsely being triggered.
      </p>

      <p>
        <b>OFF:</b> Turning fall detection off means that the Mobile Companion will NOT
        automatically detect falls and all emergencies must be triggered by button press on the
        device. This is not recommended but will extend battery life.
      </p>

      <h4>Testing Your Device</h4>

      <p>
        We recommend testing these device settings with your loved ones by dropping the Mobile
        Companion at varying heights/speeds against different surfaces. Adjust settings as
        appropriate for your needs.*
      </p>

      <h4>Updating Settings</h4>
      <p>
        Please note: it can take up to 5 minutes for your new settings to update on this device.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>

    <b-modal v-if="modal == 'RF_BUTTON'" v-model="open" centered>
      <h2 class="mb-4">Care Button Settings</h2>

      <h4>Button Press</h4>

      <p>
        <b>Single button press - 1x:</b> On this setting, the device will trigger an emergency with
        one press.
      </p>

      <p>
        <b>Repeated button press - 3+ times:</b> On this setting, the device should be
        <b>fully pressed and released</b> at least 3 times within a 10 second window to trigger the
        device.
      </p>

      <h4>Button Press Settings</h4>
      <p>
        This is how many times the Care Button needs to be pressed in order to trigger an emergency
        response.
      </p>
      <p>
        The device is defaulted to 1x but if you find that the care recipient is triggering false
        emergencies frequently, it may be best to change this requirement to repeated button press.
      </p>

      <h4>Testing Your Device</h4>
      <p>
        We recommend testing these device settings with your loved ones by selecting each of the
        button press settings and pressing the button at the required interval.
      </p>
      <p>
        To avoid calling Emergency Services when testing, put your system on Learning Mode,
        accessible by clicking the icon on the upper right corner of Device Management. Adjust
        settings as appropriate for your needs.
      </p>
      <template #modal-footer="{ok, cancel, hide}">
        <b-btn variant="primary" @click="hide()" class="btn-block" size="lg">Okay</b-btn>
      </template>
    </b-modal>
  </span>
</template>

<style lang="scss" scoped>
@import '@/assets/css/_variables.scss';

p {
  font-size: 120%;
  color: $body-color;
}
</style>
